import Box from "@material-ui/core/Box";
import {grey} from "@material-ui/core/colors";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import {XYCoord} from "dnd-core";
import React, {forwardRef, ReactNode, useImperativeHandle, useRef} from "react";
import {
  ConnectDragPreview,
  ConnectDragSource,
  ConnectDropTarget,
  DragSource,
  DragSourceConnector,
  DragSourceMonitor,
  DropTarget,
  DropTargetConnector,
  DropTargetMonitor,
} from "react-dnd";
import clsx from "clsx";
import useStyles from "./styles";

export const ItemTypes = {
  ITEM: "item",
};

export type DraggableItemDragObject = {
  id: string;
  index: number;
};

export type RenderCustomItemProps = {
  isDragging: boolean;
  previewRef: React.MutableRefObject<null>;
  dragRef: React.MutableRefObject<null>;
};

export type DraggableItemProps = {
  id: any;
  view: ReactNode;
  disabled?: boolean;
  index: number;
  isDragging: boolean;
  bodyClassName?: string;
  onEndDragging: (dragIndex: number, hoverIndex: number) => void;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  renderCustomItem?: (args: RenderCustomItemProps) => React.ReactElement;
  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;
  connectDragPreview: ConnectDragPreview;
  itemClassName?: string;
};

type DraggableItemInstance = {
  getNode(): HTMLDivElement | null;
};

const DraggableItem = forwardRef<HTMLDivElement, DraggableItemProps>(
  (
    {
      view,
      bodyClassName,
      isDragging,
      connectDragSource,
      connectDropTarget,
      connectDragPreview,
      renderCustomItem,
      itemClassName,
      disabled,
    },
    ref
  ) => {
    const classes = useStyles();
    const opacity = isDragging ? 0.2 : 1;
    const background = isDragging ? grey[400] : undefined;
    const previewRef = useRef(null);
    const dragRef = useRef(null);

    connectDragSource(dragRef);
    connectDropTarget(dragRef);
    connectDragPreview(previewRef);

    useImperativeHandle<any, DraggableItemInstance>(ref, () => ({
      getNode: () => previewRef.current,
    }));

    return renderCustomItem ? (
      renderCustomItem({previewRef, dragRef, isDragging})
    ) : (
      <div
        ref={previewRef}
        className={clsx(classes.item, itemClassName)}
        style={{opacity, background}}
      >
        <div
          ref={dragRef}
          className={clsx(classes.cursor, disabled && classes.cursorDisabled)}
        >
          <DragHandleIcon />
        </div>
        <Box className={clsx(classes.body, bodyClassName)}>{view}</Box>
      </div>
    );
  }
);

export default DropTarget(
  ItemTypes.ITEM,
  {
    hover(
      props: DraggableItemProps,
      monitor: DropTargetMonitor,
      component: DraggableItemInstance
    ) {
      if (!component) {
        return null;
      }
      // node = HTML Div element from imperative API
      const node = component.getNode();
      if (!node) {
        return null;
      }

      const dragIndex = monitor.getItem<DraggableItemDragObject>().index;
      const hoverIndex = props.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = node.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      props.moveItem(dragIndex, hoverIndex);
      monitor.getItem<DraggableItemDragObject>().index = hoverIndex;
    },
  },
  (connect: DropTargetConnector) => ({
    connectDropTarget: connect.dropTarget(),
  })
)(
  DragSource(
    ItemTypes.ITEM,
    {
      canDrag: (props: DraggableItemProps) => {
        if (props.disabled) {
          return false;
        }

        return true;
      },
      beginDrag: (props: DraggableItemProps) => ({
        id: props.id,
        index: props.index,
      }),
      endDrag: (props, monitor) => {
        const dragIndex = monitor.getItem<DraggableItemDragObject>().index;
        const hoverIndex = props.index;
        props.onEndDragging(dragIndex, hoverIndex);
      },
    },
    (connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
      connectDragSource: connect.dragSource(),
      connectDragPreview: connect.dragPreview(),
      isDragging: monitor.isDragging(),
    })
  )(DraggableItem)
);
